import { ref } from "vue";

/** View-as / Preview eligibility helpers (ER-10317 / idea-MDP-503). */

export const VIEW_AS_MODE = "view_as";
export const PREVIEW_MODE = "preview";

const ADMIN_PERMISSIONS = new Set(["admin", "administrator", "co-owner", "owner"]);
const NON_ACTIVE_ACCESS_STATUSES = new Set(["suspended", "blocked", "closed"]);

/**
 * Shared reactive eligibility so listing menus refresh after App.vue finishes
 * async `syncAccessPermission()` (Co-Owner / Admin were stuck false at mount).
 */
export const canStartViewAsRef = ref(false);

let accessListenerBound = false;

/** Current signed-in access may start view-as/preview (Administrator and above). */
export function currentUserCanStartViewAs() {
  try {
    const access = JSON.parse(localStorage.getItem("accessPermission") || "{}");
    const permission = String(access?.admin_permission || "").toLowerCase();
    return ADMIN_PERMISSIONS.has(permission);
  } catch {
    return false;
  }
}

/** Re-read localStorage into the shared ref (call after access permission sync). */
export function refreshCanStartViewAs() {
  canStartViewAsRef.value = currentUserCanStartViewAs();
  return canStartViewAsRef.value;
}

/**
 * Keep `canStartViewAsRef` in sync when App.vue dispatches
 * `exiqtive-access-filters-changed` after patching accessPermission.
 */
export function bindCanStartViewAsAccessListener() {
  if (accessListenerBound || typeof window === "undefined") return;
  accessListenerBound = true;
  refreshCanStartViewAs();
  window.addEventListener("exiqtive-access-filters-changed", refreshCanStartViewAs);
}

/**
 * Which row action to show for an employee: view_as, preview, or null.
 * Impersonate (view_as) and Preview are never shown together.
 */
export function getEmployeeViewAsAction(employee, status) {
  if (!employee) return null;
  const empStatus = String(status || employee.status || employee.current_status || "").toLowerCase();
  if (empStatus !== "active" || employee.archived) return null;

  const access = employee.access;
  if (!access) {
    // hasAccess without nested access cannot start Impersonate (no access id) and
    // is not a true no-Access row for Preview either.
    if (employee.hasAccess || employee.has_access) return null;
    return PREVIEW_MODE;
  }

  // Any Administrator-or-above user may impersonate any employee with Access,
  // including other admins/owners/co-owners and themselves (ER-10317).

  // Hide Impersonate when we cannot call view-as start (missing access id).
  const accessId = access.id ?? access.access_id;
  if (accessId == null) return null;

  return VIEW_AS_MODE;
}

/** Target access status for entry modal / banner: suspended | blocked | closed | "". */
export function getViewAsTargetStatus(employee) {
  if (!employee) return "";
  if (employee.isSuspended) return "suspended";

  const raw = String(
    employee.accessStatus || employee.access?.employee_status || employee.access?.user?.state || ""
  ).toLowerCase();

  if (NON_ACTIVE_ACCESS_STATUSES.has(raw)) return raw;
  return "";
}

export function getEmployeeDisplayName(employee) {
  if (!employee) return "employee";
  const full = `${employee.first_name || ""} ${employee.last_name || ""}`.trim();
  return full || employee.email || "employee";
}

export function getEmployeeAccessId(employee) {
  const access = employee?.access;
  if (!access) return null;
  return access.id ?? access.access_id ?? null;
}
